import { storyblokEditable } from '@storyblok/react';
import Image from 'next/image';
import { FC } from 'react';

import Container from '@/components/elements/Container';
import HeadingLayout from '@/components/elements/Heading';
import PreHeading from '@/components/elements/PreHeading';
import Section from '@/components/elements/Section';
import Text from '@/components/elements/Text';

export interface HeroStoryProps {
  story: any;
  blok: any;
  heading?: string;
  content?: string;
  category?: object;
  image?: object;
  video?: object;
  children?: any;
}

const HeroStory: FC<HeroStoryProps> = ({
  story,
  blok,
  heading = blok.title ? blok.title : story.name,
  category = blok.tags,
  content = blok.preview,
  image = blok.hero_feature_image,
  children,
}) => {
  const publishDate = new Date(story.first_published_at);
  const publishDateString = publishDate.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const url = new URL(image?.filename);
  const sizeString = url.pathname.split('/')[3];
  const width = parseInt(sizeString.split('x')[0], 10);
  const height = parseInt(sizeString.split('x')[1], 10);

  return (
    <Section gapBottom="xl" {...storyblokEditable(blok)} className="relative">
      {children}
      <Container size="full">
        <div className="mx-auto max-w-[115ch] space-y-8 py-16 text-center">
          <div className="flex flex-col-reverse items-center justify-center gap-y-4">
            <HeadingLayout size="2xl" weight="medium">
              {heading}
            </HeadingLayout>
            <PreHeading element="p" size="md" className="text-center">
              {category.name}
            </PreHeading>
          </div>
          {story.first_published_at && (
            <PreHeading color="lighter" size="xs">
              {publishDateString}
            </PreHeading>
          )}
        </div>
      </Container>

      <div className="space-y-24">
        {content && (
          <div className="mt-12">
            <Container>
              <div className="mx-auto max-w-7xl">
                <Text size="lg" color="black">
                  {content}
                </Text>
              </div>
            </Container>
          </div>
        )}
        {image?.filename && (
          <Container size="3xl">
            <div className="relative aspect-[16/9]">
              <Image
                alt={image?.alt}
                src={image?.filename}
                width={width}
                height={height}
                priority
                className="w-full"
              />
            </div>
            <p className="!m-0 pt-2 font-mono text-xs text-neutral-500 empty:hidden lg:text-right">
              {image?.title && `${image?.title}`}
              {image?.copyright && ` © ${image?.copyright}`}
              {image?.source && ` / ${image?.source}`}
            </p>
          </Container>
        )}
      </div>
    </Section>
  );
};

export default HeroStory;
